import { useRef } from 'react';
import { useDateNow, useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { addDays, format } from 'date-fns';
import { toDate } from 'date-fns-tz';
import { Alert, ModalButtonWithForm, TargetSchedule } from 'components/common';
import { FormGroup, Select } from 'components/common/form';

import type { FormApi, FormState } from 'informed';
import type { Deadline } from 'shared-types';
import type { AddGroupCourseReq } from 'shared-types/requests';
import type { GetGroupCoursesRes } from 'shared-types/responses';

type FormValues = {
  courseId?: number;
  deadlineDate: number | Date;
  deadlineDurationUnit: Deadline['deadlineDurationUnit'];
  deadlineDurationValue: number;
  deadlineType: Deadline['deadlineType'];
  isMandatory: boolean;
};

interface Props {
  administeredCourses: GetGroupCoursesRes['administeredCourses'];
  updateCourses: () => Promise<void>;
}

const GroupCoursesAdd = ({ administeredCourses, updateCourses }: Props) => {
  const { dateNow: now } = useDateNow();

  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { group },
    serverConfig: { timeZone }
  } = useReduxState();

  const formApi = useRef<FormApi<FormValues>>(undefined);

  const getCourse = (courseId?: number) =>
    administeredCourses.find((course) => course.id === courseId);

  // a course that is mandatory for everyone shows the switch as on, the same
  // way the group's target schedule modal does
  const handleCourseChange = (courseId: number) => {
    formApi.current?.setValue(
      'isMandatory',
      Boolean(getCourse(courseId)?.isMandatory)
    );
  };

  const submit = async ({
    courseId,
    deadlineDate,
    deadlineDurationUnit,
    deadlineDurationValue,
    deadlineType,
    isMandatory
  }: FormValues) => {
    await api.post(`/admin/groups/${group.id}/courses`, {
      courseId,
      ...(getCourse(courseId)?.isMandatory ? {} : { isMandatory }),
      deadlineDate: deadlineDate
        ? toDate(`${format(deadlineDate, 'y-MM-dd')}T23:59:59`, {
            timeZone
          }).toISOString()
        : null,
      deadlineDurationUnit,
      deadlineDurationValue,
      deadlineType
    } satisfies AddGroupCourseReq);
    await updateCourses();

    dispatch.addNotice({
      text: messages.commonAddCourseSuccess,
      type: 'success'
    });
  };

  const initialValues = {
    deadlineDate: addDays(now, 1),
    deadlineDurationUnit: 'days' as const,
    deadlineDurationValue: 7,
    deadlineType: 'course-default' as const,
    isMandatory: Boolean(administeredCourses?.[0]?.isMandatory),
    ...(administeredCourses?.length
      ? { courseId: administeredCourses[0].id }
      : {})
  } satisfies FormValues;

  return !administeredCourses ? null : (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonIcon="plus"
      buttonSize="header"
      apiRef={formApi}
      buttonText={messages.commonAddCourse}
      initialValues={initialValues}
      modalActionText={messages.commonAdd}
      modalTitle={messages.commonAddCourse}
      onSubmit={submit}
    >
      {(formState: FormState<FormValues>) => {
        const course = getCourse(formState.values.courseId);

        return administeredCourses && administeredCourses.length ? (
          <>
            <FormGroup htmlFor="courseId" label={messages.commonCourse}>
              <Select
                field="courseId"
                id="courseId"
                isSearchable={true}
                onChange={handleCourseChange}
                options={administeredCourses.map((course) => ({
                  label: course.title,
                  value: course.id
                }))}
              />
            </FormGroup>

            {course ? (
              <TargetSchedule
                id="group-courses-add"
                includeCourseDefaultOption={true}
                includeCourseDefaultValue={false}
                includeMandatoryOption={true}
                mandatoryDisabledReasons={
                  course.isMandatory
                    ? [messages.courseMandatoryReasonCourse]
                    : []
                }
              />
            ) : null}
          </>
        ) : (
          <Alert text={messages.groupsCoursesAddEmpty} />
        );
      }}
    </ModalButtonWithForm>
  );
};

export default GroupCoursesAdd;
